// Iter60A — Floor Plans public marketplace (/floor-plans)
// Extends the existing Browse Plans experience. Reuses PlanCard for the
// grid but requests channel=floor_plan so premium plans stay isolated on
// /plans. Colored floor plan is prioritized as the card hero via the
// helper in @/lib/planMedia.
import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import PlanCard from "@/components/PlanCard";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

const FloorPlans = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const style = searchParams.get("style") || "All";
  const minBeds = searchParams.get("min_bedrooms") || "";
  const search = searchParams.get("search") || "";

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [styles, setStyles] = useState([]);
  const [searchDraft, setSearchDraft] = useState(search);

  useEffect(() => { setSearchDraft(search); }, [search]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/styles");
        const list = Array.isArray(data) ? data.map((s) => (typeof s === "string" ? s : s.name)) : (data?.styles || []);
        setStyles(["All", ...list]);
      } catch (err) { console.warn("[floor-plans] styles load failed", err); }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { channel: "floor_plan", limit: 50 };
        if (style && style !== "All") params.style = style;
        if (minBeds) params.min_bedrooms = minBeds;
        if (search) params.search = search;
        const { data } = await api.get("/plans", { params });
        if (!cancelled) setPlans(Array.isArray(data) ? data : (data?.items || []));
      } catch (e) {
        if (!cancelled) toast.error("Could not load floor plans", { description: e.message });
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [style, minBeds, search]);

  const patch = (obj) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(obj).forEach(([k, v]) => {
      if (!v || v === "All") next.delete(k); else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({}, { replace: true });
  const hasFilters = style !== "All" || minBeds || search;

  const gridBody = useMemo(() => {
    if (loading) return <div className="py-24 text-center text-sm text-muted-foreground">Loading floor plans…</div>;
    if (plans.length === 0) {
      return (
        <div className="py-24 text-center border border-dashed border-border" data-testid="floor-plans-empty">
          <div className="text-lg font-display mb-2">No floor plans match your filters</div>
          <div className="text-sm text-muted-foreground mb-4">Try widening the search or clearing filters.</div>
          {hasFilters && <button onClick={clearAll} className="px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.14em]" data-testid="floor-plans-clear">Clear filters</button>}
        </div>
      );
    }
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="floor-plans-grid">
        {plans.map((p) => <PlanCard key={p.id} plan={p} floorPlanFirst channel="floor_plan" />)}
      </div>
    );
  }, [plans, loading, hasFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14" data-testid="floor-plans-page">
        {/* Hero */}
        <div className="mb-10">
          <div className="overline text-accent">New</div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mt-2" data-testid="floor-plans-title">
            Floor Plans
          </h1>
          <p className="mt-3 text-base md:text-lg text-muted-foreground max-w-2xl">
            Ready-made floor plans for buyers who want a layout first. Instant digital delivery — PDF, CAD, and Revit files without the wait.
          </p>
        </div>

        {/* Filter bar */}
        <div className="border border-border bg-white p-4 mb-8 flex flex-wrap gap-3 items-end" data-testid="floor-plans-filters">
          <div className="flex-1 min-w-[220px]">
            <label className="overline text-muted-foreground block mb-1">Search</label>
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") patch({ search: searchDraft }); }}
                onBlur={() => searchDraft !== search && patch({ search: searchDraft })}
                placeholder="Search by name or feature…"
                className="w-full h-10 pl-9 pr-3 border border-input bg-background text-sm"
                data-testid="floor-plans-search"
              />
            </div>
          </div>
          <div>
            <label className="overline text-muted-foreground block mb-1">Style</label>
            <select
              value={style}
              onChange={(e) => patch({ style: e.target.value })}
              className="h-10 px-3 border border-input bg-background text-sm min-w-[160px]"
              data-testid="floor-plans-style"
            >
              {styles.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="overline text-muted-foreground block mb-1">Bedrooms</label>
            <select
              value={minBeds}
              onChange={(e) => patch({ min_bedrooms: e.target.value })}
              className="h-10 px-3 border border-input bg-background text-sm min-w-[120px]"
              data-testid="floor-plans-beds"
            >
              <option value="">Any</option>
              <option value="1">1+</option>
              <option value="2">2+</option>
              <option value="3">3+</option>
              <option value="4">4+</option>
              <option value="5">5+</option>
            </select>
          </div>
          {hasFilters && (
            <button onClick={clearAll} className="h-10 px-3 border border-border text-xs uppercase tracking-[0.14em] hover:border-primary flex items-center gap-1" data-testid="floor-plans-clear-bar">
              <X size={12} /> Clear
            </button>
          )}
          <div className="ml-auto text-xs text-muted-foreground pb-2" data-testid="floor-plans-count">
            {loading ? "" : `${plans.length} result${plans.length === 1 ? "" : "s"}`}
          </div>
        </div>

        {gridBody}
      </div>
    </Layout>
  );
};

export default FloorPlans;
